import React, { useCallback, useEffect, useState } from "react";
import { Form, Input, Space, Tag, Descriptions, Row, Col, Typography } from "antd";
import { LockOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Button, Card, PageHeader, Skeleton } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import TwoFactorCard from "./TwoFactorCard";

const { Text } = Typography;

const ROLE_COLOR = {
  Owner: "magenta",
  Admin: "red",
  Developer: "blue",
  Client: "green",
};

/**
 * The account's own profile: the details it may edit about itself, plus the security
 * controls (password, authenticator).
 *
 * Every request here goes to /api/account/*, which derives the user from the token — the
 * page has no notion of a user id, so it cannot be pointed at anyone else. It is also the
 * one screen an account still owing an authenticator can reach, which is why the layout
 * puts security beside the details rather than behind a tab.
 */
const Profile = () => {
  const navigate = useNavigate();
  const { mutate, loading } = useMutations();
  const [form] = Form.useForm();
  const [profile, setProfile] = useState(null);

  const load = useCallback(
    () =>
      mutate("loadProfile", {
        method: "get",
        endpoint: "/account/profile",
        onSuccess: (data) => {
          setProfile(data);
          form.setFieldsValue({
            displayName: data?.displayName,
            email: data?.email,
            phoneNumber: data?.phoneNumber,
            jobTitle: data?.jobTitle,
          });
        },
      }),
    [mutate, form]
  );

  useEffect(() => {
    load();
    // `load` is stable enough for a mount-time fetch; re-running it on every render of
    // the mutation hook would put the page in a request loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async (values) => {
    await mutate("saveProfile", {
      method: "put",
      endpoint: "/account/profile",
      payload: values,
      onSuccess: (data) => setProfile(data),
    });
  };

  if (!profile) {
    return (
      <div>
        <PageHeader title="My profile" />
        <Card>
          <Skeleton rows={6} />
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="My profile"
        subtitle="Your details and how you sign in."
        extra={
          <Button
            variant="secondary"
            icon={<LockOutlined />}
            onClick={() => navigate("/change-password")}
          >
            Change password
          </Button>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Details">
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Username">
                  <Text strong>{profile.username}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Role">
                  <Tag color={ROLE_COLOR[profile.role] || "default"}>{profile.role}</Tag>
                </Descriptions.Item>
                {profile.companyName && (
                  <Descriptions.Item label="Company">{profile.companyName}</Descriptions.Item>
                )}
                <Descriptions.Item label="Last sign-in">
                  {profile.lastLoginUtc
                    ? dayjs(profile.lastLoginUtc).format("DD MMM YYYY HH:mm")
                    : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Password changed">
                  {profile.passwordUpdatedAt
                    ? dayjs(profile.passwordUpdatedAt).format("DD MMM YYYY HH:mm")
                    : "—"}
                </Descriptions.Item>
              </Descriptions>

              {/* Username, role and company are administration, not self-service — they are
                  shown above and are absent from the form on purpose. */}
              <Form form={form} layout="vertical" onFinish={onSave}>
                <Form.Item name="displayName" label="Display name">
                  <Input placeholder="How your name appears in the app" />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[{ type: "email", message: "That email address is not valid" }]}
                >
                  <Input placeholder="name@company.com" />
                </Form.Item>
                <Form.Item name="phoneNumber" label="Phone">
                  <Input placeholder="+383 …" />
                </Form.Item>
                <Form.Item name="jobTitle" label="Job title">
                  <Input placeholder="e.g. Backend developer" />
                </Form.Item>

                <Button
                  variant="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading?.saveProfile}
                >
                  Save changes
                </Button>
              </Form>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <TwoFactorCard status={profile.twoFactor} onChanged={load} />
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
